import type { ProcessModelValidationSeverity } from "@ankaadia/ankaadia-shared";
import type {
  ProcessAreaModelEntityFragment,
  ProcessPhaseModelEntityFragment,
  ProcessStageInfo,
  ProcessTaskModelResultFragment,
} from "@ankaadia/graphql";
import { PrimeIcons } from "primeng/api";
import type { SupportedTaskTypes } from "../../../organization-specific/organization-specific";

export const newNodeId = "new";

export type ProcessTaskModelResultFragmentWithPreviousTaskId = ProcessTaskModelResultFragment & {
  previousTaskId?: string;
};
export type ProcessAreaModelEntityFragmentWithPreviousAreaId = ProcessAreaModelEntityFragment & {
  previousAreaId?: string;
};
export type ProcessPhaseModelEntityFragmentWithPreviousPhaseId = ProcessPhaseModelEntityFragment & {
  previousPhaseId?: string;
};

export type NodeDataModelType =
  | ProcessStageInfo
  | ProcessAreaModelEntityFragment
  | ProcessPhaseModelEntityFragment
  | ProcessTaskModelResultFragment;

export interface INodeData {
  model: NodeDataModelType;
  operations: { label: string; icon: string; action: (x: any, e?: Event) => void }[];
  typeDisplayName: string;
  typeStyleClass: string;
  textStyleClass: string;
  tagStyleClass: string;
  validationMessage?: string;
  validationSeverity?: ProcessModelValidationSeverity;
}

export type ProcessNodeLevel = "process" | "area" | "phase" | "task";

const processTypeNames: ReadonlySet<string> = new Set([
  "ProcessModel",
  "ProcessModelEntity",
  "ProcessModelExtension",
  "ProcessModelExtensionEntity",
  "ProcessModelInfo",
  "ProcessStageInfo",
]);

const areaTypeNames: ReadonlySet<string> = new Set(["ProcessAreaModel", "ProcessAreaModelEntity"]);

const phaseTypeNames: ReadonlySet<string> = new Set(["ProcessPhaseModel", "ProcessPhaseModelEntity"]);

export function getNodeLevel(model: { _typeName?: string; __typename?: string }): ProcessNodeLevel {
  const typeName = model._typeName ?? model.__typename;
  if (processTypeNames.has(typeName)) return "process";
  if (areaTypeNames.has(typeName)) return "area";
  if (phaseTypeNames.has(typeName)) return "phase";
  return "task";
}

export function getNodeTypeStyleClass(model: { _typeName?: string; __typename?: string }): string {
  switch (getNodeLevel(model)) {
    case "process":
      return "pm-tag-indigo";
    case "area":
      return "pm-tag-green";
    case "phase":
      return "pm-tag-purple";
    case "task":
      return "pm-tag-gray";
  }
}

export function getNodeTextStyleClass(model: { _typeName?: string; __typename?: string }): string {
  switch (getNodeLevel(model)) {
    case "process":
      return "pm-font-bold";
    case "area":
      return "pm-font-semibold";
    case "phase":
      return "pm-font-medium";
    case "task":
      return "pm-font-normal";
  }
}

export function getNodeTagStyleClass(model: { _typeName?: string; __typename?: string }): string {
  return `${getNodeTypeStyleClass(model)} ${getNodeTextStyleClass(model)}`;
}

export const ProcessTaskIcon: Record<SupportedTaskTypes, string> = {
  ProcessTaskAutoOperationModel: PrimeIcons.BOLT,
  ProcessTaskTriggerEventModel: PrimeIcons.WAVE_PULSE,
  ProcessTaskAssignmentExceptionModel: PrimeIcons.EJECT,
  ProcessTaskDeactivateProcessModel: PrimeIcons.BRIEFCASE,
  ProcessTaskDocumentCheckModel: PrimeIcons.FILE_CHECK,
  ProcessTaskEnterMissingInformationModel: PrimeIcons.PEN_TO_SQUARE,
  ProcessTaskInformationCollectionModel: PrimeIcons.FILE_EDIT,
  ProcessTaskInformationSendOutModel: PrimeIcons.SEND,
  ProcessTaskInviteToPlatformModel: PrimeIcons.USER_PLUS,
  ProcessTaskPdfFormGenerationModel: PrimeIcons.FILE_PDF,
  ProcessTaskOnlineApplicationModel: PrimeIcons.WAREHOUSE,
  ProcessTaskUpdateCandidateProfileModel: PrimeIcons.ID_CARD,
  ProcessTaskUploadModel: PrimeIcons.UPLOAD,
  ProcessTaskEmailSendModel: PrimeIcons.ENVELOPE,
  ProcessTaskWorkModel: PrimeIcons.CHECK,
  ProcessTaskReminderModel: PrimeIcons.BELL,
  ProcessTaskRoleSetupModel: PrimeIcons.USERS,
  ProcessTaskOrganizationAuditModel: PrimeIcons.BUILDING,
  ProcessTaskEnterMissingInformationV2Model: PrimeIcons.PEN_TO_SQUARE,
  ProcessTaskTranslateDocumentsModel: PrimeIcons.LANGUAGE,
  ProcessTaskUploadAvatarModel: PrimeIcons.IMAGE,
  ProcessTaskInterviewWriteModel: PrimeIcons.HAMMER,
  ProcessTaskEducationAssignToExamModel: PrimeIcons.GRADUATION_CAP,
  ProcessTaskEducationEnterExamResultModel: PrimeIcons.HEART,
};
